/**
 * Datadog RUM Integration for Billing MFE
 * 
 * This module supports two modes:
 * 
 * 1. **Standalone Mode** (Development):
 *    - Initializes its own Datadog instance
 *    - Uses environment variables: DATADOG_RUM_APPLICATION_ID and DATADOG_RUM_CLIENT_TOKEN
 * 
 * 2. **Module Federation Mode** (Production):
 *    - Uses Datadog instance from host application
 *    - Host should call `setDatadogInstance(datadogRum)` when loading the MFE
 * 
 * Usage in host application:
 * ```typescript
 * import { datadogRum } from '@datadog/browser-rum';
 * import { setDatadogInstance } from 'billingMfe/datadog';
 * 
 * // After initializing Datadog in the host
 * setDatadogInstance(datadogRum);
 * ```
 */
import { datadogRum } from '@datadog/browser-rum';
import type { RumGlobal } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

const applicationId = process.env.DATADOG_RUM_APPLICATION_ID;
const clientToken = process.env.DATADOG_RUM_CLIENT_TOKEN;
const nodeEnv = process.env.NODE_ENV;

let isDatadogInitialized = false;
let initializationAttempted = false;
let externalDatadogInstance: RumGlobal | null = null;

/**
 * Set an external Datadog RUM instance (from host application).
 * This should be called when the MFE is loaded in a Module Federation context.
 * @param instance - The Datadog RUM instance from the host application
 */
export function setDatadogInstance(instance: RumGlobal) {
  console.log('[Datadog MFE] 🔗 Using Datadog instance provided by host application');
  externalDatadogInstance = instance;
  isDatadogInitialized = true;
  initializationAttempted = true;
}

/**
 * Initialize Datadog RUM if not already initialized.
 * This function can be called multiple times safely - it will only initialize once.
 * It works in both standalone mode and Module Federation contexts.
 */
function ensureDatadogInitialized() {
  // If already attempted initialization, return cached result
  if (initializationAttempted) {
    return isDatadogInitialized;
  }

  initializationAttempted = true;

  // First, check if Datadog is already initialized (by host app or another module)
  // In Module Federation, the host app will typically have Datadog already initialized
  try {
    const existingSession = datadogRum.getInternalContext();
    if (existingSession) {
      console.log('[Datadog MFE] ✅ Using existing Datadog RUM instance from host application');
      console.log('[Datadog MFE] Session ID:', existingSession.session_id);
      isDatadogInitialized = true;
      return true;
    }
  } catch (e) {
    console.log('[Datadog MFE] No existing Datadog instance found, will initialize new one');
  }

  // If no existing instance, try to initialize our own (standalone mode)
  console.log('[Datadog MFE] Configuration:', {
    applicationId: applicationId ? `${applicationId.substring(0, 8)}...` : 'undefined',
    clientToken: clientToken ? `${clientToken.substring(0, 8)}...` : 'undefined',
    nodeEnv,
    hasApplicationId: !!applicationId,
    hasClientToken: !!clientToken,
  });

  if (applicationId && clientToken) {
    try {
      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: 'billing-mfe',
        env: nodeEnv || 'development',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
        trackViewsManually: true,
      });
      datadogLogs.init({
        site: "datadoghq.com",
        sessionSampleRate: 100,
        env: nodeEnv || 'development',
        service: "billing-mfe",
        clientToken: clientToken,
        forwardErrorsToLogs: true,
      });
      isDatadogInitialized = true;
      console.log('[Datadog MFE] ✅ Successfully initialized new Datadog instance');
    } catch (error: any) {
      console.warn('[Datadog MFE] Failed to initialize new instance:', error.message);
      // Even if init fails, Datadog might already be initialized
      // This can happen if the host initialized it between our checks
      try {
        const existingSession = datadogRum.getInternalContext();
        if (existingSession) {
          console.log('[Datadog MFE] ✅ Using existing instance (found after failed init)');
          isDatadogInitialized = true;
        }
      } catch (e) {
        console.error('[Datadog MFE] ❌ Datadog is not available');
      }
    }
  } else {
    console.warn('[Datadog MFE] ⚠️ Missing credentials - Datadog RUM will not be initialized');
    console.warn('[Datadog MFE] applicationId present:', !!applicationId);
    console.warn('[Datadog MFE] clientToken present:', !!clientToken);
  }

  return isDatadogInitialized;
}

// Attempt initialization immediately for standalone mode
ensureDatadogInitialized();

export async function loadDatadog() {
  if (!isDatadogInitialized) {
    console.warn('[Datadog] loadDatadog called but Datadog is not initialized');
    return Promise.resolve(undefined);
  }
  return new Promise((resolve) => {
    datadogRum.onReady(() => resolve(undefined));
  });
}

export async function trackEventDatadog(
  event: string,
  properties?: Record<string, any>,
) {
  // Use external instance if provided, otherwise use the global one
  const ddInstance = externalDatadogInstance || datadogRum;

  // Ensure Datadog is initialized before tracking (only if using global instance)
  if (!externalDatadogInstance) {
    ensureDatadogInitialized();
  }

  if (!isDatadogInitialized) {
    console.warn(`[Datadog MFE] ⚠️ trackEventDatadog called but Datadog is not initialized. Event: ${event}`, properties);
    return;
  }

  try {
    // Add MFE context to all events to distinguish them from host app events
    const enrichedProperties = {
      ...properties,
      source: 'billing-mfe',
      mfe_version: process.env.NODE_ENV,
    };

    // Get internal context for debugging
    const context = ddInstance.getInternalContext();
    console.log(`[Datadog MFE] 📊 Tracking event: ${event}`, {
      properties: enrichedProperties,
      sessionId: context?.session_id,
      viewId: context?.view?.id,
      usingExternalInstance: !!externalDatadogInstance,
    });

    ddInstance.addAction(event, enrichedProperties);
    console.log(`[Datadog MFE] ✅ Event successfully sent: ${event}`);
  } catch (error: any) {
    console.error(`[Datadog MFE] ❌ Failed to track event: ${event}`, error.message, error);
  }
}
